/**
 * What the "which account?" ask DOES (2026-09-03 owner decision), apart from how it looks (PV14 part 2a, 2026-10-09):
 * open, type, save, keep what was typed when the save fails, dismiss. ./IdentityPrompt.tsx draws it for the
 * integrations page and today's onboarding; the new design's onboarding draws the same hook, so the ask cannot start
 * answering differently on two pages. Its words are here too, so the new design reads them without today's drawing.
 *
 * WHAT IT PROMISES (./IdentityPrompt.tsx says why it exists):
 *  - It is never a gate. Dismissing it costs nothing and it does not come back within the session.
 *  - It never guesses. The value written is the one the person typed.
 *  - A failed save says so and keeps what was typed, rather than silently discarding it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Code-authored UI copy, {en, es} like every other line these surfaces
 *  author. "Prest0n" keeps its capital P (2026-08-22 naming rule). */
// copy: connect_identity_prompt_chrome
export const IDENTITY_PROMPT_COPY = {
  ask: { en: 'which account?', es: '¿qué cuenta?' },
  placeholder: { en: 'email or username', es: 'correo o usuario' },
  save: { en: 'save', es: 'guardar' },
  saving: { en: 'saving…', es: 'guardando…' },
  cancel: { en: 'not now', es: 'ahora no' },
  failed: {
    en: "couldn't save that just now — try again",
    es: 'no se pudo guardar — inténtalo otra vez',
  },
} as const;

export type IdentityPromptLang = 'en' | 'es';

export interface IdentityPromptState {
  open: boolean;
  /** Opens the field (the ask's chip). */
  openAsk: () => void;
  value: string;
  setValue: (value: string) => void;
  saving: boolean;
  failed: boolean;
  /** Save what was typed; an empty answer is a dismissal, never a write. */
  submit: () => Promise<void>;
  /** Not now: closes, and tells the caller the ask was dismissed. */
  cancel: () => void;
}

export function useIdentityPrompt({ slug, onSave, onDismiss }: {
  slug: string;
  onSave: (slug: string, identity: string) => Promise<boolean>;
  onDismiss?: (slug: string) => void;
}): IdentityPromptState {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // A save that resolves after the row is gone must not set state on a
  // component nobody is looking at.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const submit = useCallback(async () => {
    const identity = value.trim();
    // An empty submit is a dismissal, not a write: clearing an identity is a
    // deliberate act on a connection that HAS one, and this prompt only ever
    // appears on connections that do not.
    if (!identity) {
      setOpen(false);
      onDismiss?.(slug);
      return;
    }
    setSaving(true);
    setFailed(false);
    let ok = false;
    try {
      ok = await onSave(slug, identity);
    } catch {
      ok = false;
    }
    if (!aliveRef.current) return;
    setSaving(false);
    if (ok) {
      setOpen(false);
      return;
    }
    // Keep what they typed — making someone retype an address because our
    // write failed is our mistake charged to them.
    setFailed(true);
  }, [onSave, onDismiss, slug, value]);

  const openAsk = useCallback(() => setOpen(true), []);
  const cancel = useCallback(() => { setOpen(false); onDismiss?.(slug); }, [onDismiss, slug]);
  return { open, openAsk, value, setValue, saving, failed, submit, cancel };
}
